/** biome-ignore-all lint/performance/noNamespaceImport: import shadcn components */
'use client';

import type { Column, Table } from '@tanstack/react-table';
import {
  BadgeCheck,
  CalendarIcon,
  Check,
  ListFilter,
  Text as TextIcon,
  X,
} from 'lucide-react';
import { useQueryState } from 'nuqs';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { DataTableRangeFilter } from '@/components/data-table/data-table-range-filter';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { getDefaultFilterOperator, getFilterOperators } from '@/lib/data-table';
import { formatDate } from '@/lib/format';
import { generateId } from '@/lib/id';
import { getFiltersStateParser } from '@/lib/parsers';
import { cn } from '@/lib/utils';
import type {
  ExtendedColumnFilter,
  FilterOperator,
  Option,
} from '@/types/data-table';

const DEBOUNCE_MS = 300;
const THROTTLE_MS = 50;
const FILTER_SHORTCUT_KEY = 'f';
const REMOVE_FILTER_SHORTCUTS = ['backspace', 'delete'];

interface DataTableFilterMenuProps<TData>
  extends React.ComponentProps<typeof PopoverContent> {
  table: Table<TData>;
  debounceMs?: number;
  throttleMs?: number;
  shallow?: boolean;
}

export function DataTableFilterMenu<TData>({
  table,
  debounceMs = DEBOUNCE_MS,
  throttleMs = THROTTLE_MS,
  shallow = true,
  align = 'start',
  ...props
}: DataTableFilterMenuProps<TData>) {
  const id = React.useId();

  const { t } = useTranslation(['table']);

  const columns = React.useMemo(
    () =>
      table
        .getAllColumns()
        .filter((column) => column.columnDef.enableColumnFilter),
    [table],
  );

  const [open, setOpen] = React.useState(false);
  const [selectedColumn, setSelectedColumn] =
    React.useState<Column<TData> | null>(null);
  const [inputValue, setInputValue] = React.useState('');
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const onOpenChange = React.useCallback((isOpen: boolean) => {
    setOpen(isOpen);

    if (!isOpen) {
      setTimeout(() => {
        setSelectedColumn(null);
        setInputValue('');
      }, 100);
    }
  }, []);

  const onInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        REMOVE_FILTER_SHORTCUTS.includes(event.key.toLowerCase()) &&
        !inputValue &&
        selectedColumn
      ) {
        event.preventDefault();
        setSelectedColumn(null);
      }
    },
    [inputValue, selectedColumn],
  );

  const [filters, setFilters] = useQueryState(
    table.options.meta?.queryKeys?.filters ?? 'filters',
    getFiltersStateParser<TData>(columns.map((field) => field.id))
      .withDefault([])
      .withOptions({
        clearOnDefault: true,
        shallow,
        throttleMs,
      }),
  );

  const [, setPage] = useQueryState(
    table.options.meta?.queryKeys?.page ?? 'page',
    {
      shallow,
      throttleMs,
    },
  );

  const debouncedSetFilters = useDebouncedCallback(
    (value: Parameters<typeof setFilters>[0]) => {
      setPage(null);
      setFilters(value);
    },
    debounceMs,
  );

  const onFilterAdd = React.useCallback(
    (column: Column<TData>, value: string) => {
      if (!value.trim() && column.columnDef.meta?.variant !== 'boolean') {
        return;
      }

      const filterValue =
        column.columnDef.meta?.variant === 'multiSelect' ? [value] : value;

      const newFilter: ExtendedColumnFilter<TData> = {
        id: column.id as Extract<keyof TData, string>,
        value: filterValue,
        variant: column.columnDef.meta?.variant ?? 'text',
        operator: getDefaultFilterOperator(
          column.columnDef.meta?.variant ?? 'text',
        ),
        filterId: generateId({ length: 8 }),
      };

      debouncedSetFilters([...filters, newFilter]);
      setOpen(false);

      setTimeout(() => {
        setSelectedColumn(null);
        setInputValue('');
      }, 100);
    },
    [filters, debouncedSetFilters],
  );

  const onFilterRemove = React.useCallback(
    (filterId: string) => {
      const updatedFilters = filters.filter(
        (filter) => filter.filterId !== filterId,
      );
      debouncedSetFilters(updatedFilters);
      requestAnimationFrame(() => {
        triggerRef.current?.focus();
      });
    },
    [filters, debouncedSetFilters],
  );

  const onFilterUpdate = React.useCallback(
    (
      filterId: string,
      updates: Partial<Omit<ExtendedColumnFilter<TData>, 'filterId'>>,
    ) => {
      debouncedSetFilters((prevFilters) => {
        const updatedFilters = prevFilters.map((filter) => {
          if (filter.filterId === filterId) {
            return { ...filter, ...updates } as ExtendedColumnFilter<TData>;
          }
          return filter;
        });
        return updatedFilters;
      });
    },
    [debouncedSetFilters],
  );

  const onFiltersReset = React.useCallback(() => {
    debouncedSetFilters([]);
  }, [debouncedSetFilters]);

  React.useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        (event.target instanceof HTMLElement &&
          event.target.contentEditable === 'true')
      ) {
        return;
      }

      if (
        event.key.toLowerCase() === FILTER_SHORTCUT_KEY &&
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey
      ) {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const onTriggerKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (
        REMOVE_FILTER_SHORTCUTS.includes(event.key.toLowerCase()) &&
        filters.length > 0
      ) {
        event.preventDefault();
        onFilterRemove(filters.at(-1)?.filterId ?? '');
      }
    },
    [filters, onFilterRemove],
  );

  return (
    <div className='flex flex-wrap items-center gap-2'>
      {filters.map((filter) => (
        <DataTableFilterItem
          columns={columns}
          filter={filter}
          filterItemId={`${id}-filter-${filter.filterId}`}
          key={filter.filterId}
          onFilterRemove={onFilterRemove}
          onFilterUpdate={onFilterUpdate}
        />
      ))}
      {filters.length > 0 && (
        <Button
          aria-label={t('reset all filters')}
          onClick={onFiltersReset}
          variant='outline'
        >
          <X className='size-4' />
        </Button>
      )}
      <Popover onOpenChange={onOpenChange} open={open}>
        <PopoverTrigger asChild>
          <Button
            aria-label={t('open filter command menu')}
            className={cn(filters.length > 0 && '', 'font-normal')}
            onKeyDown={onTriggerKeyDown}
            ref={triggerRef}
            variant='outline'
          >
            <ListFilter className='size-4 text-muted-foreground' />
            {filters.length > 0 ? null : t('filter')}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align={align}
          className='w-full max-w-(--radix-popover-content-available-width) p-0'
          {...props}
        >
          <Command className='[&_[cmdk-input-wrapper]_svg]:hidden' loop>
            <CommandInput
              onKeyDown={onInputKeyDown}
              onValueChange={setInputValue}
              placeholder={
                selectedColumn
                  ? (selectedColumn.columnDef.meta?.label ?? selectedColumn.id)
                  : t('search fields')
              }
              ref={inputRef}
              value={inputValue}
            />
            <CommandList>
              {selectedColumn ? (
                <>
                  {selectedColumn.columnDef.meta?.options && (
                    <CommandEmpty>{t('no options found')}</CommandEmpty>
                  )}
                  <FilterValueSelector
                    column={selectedColumn}
                    onSelect={(value) => onFilterAdd(selectedColumn, value)}
                    value={inputValue}
                  />
                </>
              ) : (
                <>
                  <CommandEmpty>{t('no fields found')}</CommandEmpty>
                  <CommandGroup>
                    {columns.map((column) => (
                      <CommandItem
                        key={column.id}
                        onSelect={() => {
                          setSelectedColumn(column);
                          setInputValue('');
                          requestAnimationFrame(() => {
                            inputRef.current?.focus();
                          });
                        }}
                        value={column.id}
                      >
                        {column.columnDef.meta?.icon && (
                          <column.columnDef.meta.icon />
                        )}
                        <span className='truncate'>
                          {column.columnDef.meta?.label ?? column.id}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

type DataTableFilterItemProps<TData> = {
  filter: ExtendedColumnFilter<TData>;
  filterItemId: string;
  columns: Column<TData>[];
  onFilterUpdate: (
    filterId: string,
    updates: Partial<Omit<ExtendedColumnFilter<TData>, 'filterId'>>,
  ) => void;
  onFilterRemove: (filterId: string) => void;
};

function DataTableFilterItem<TData>({
  filter,
  filterItemId,
  columns,
  onFilterUpdate,
  onFilterRemove,
}: DataTableFilterItemProps<TData>) {
  {
    const { t } = useTranslation(['table']);
    const [showFieldSelector, setShowFieldSelector] = React.useState(false);
    const [showOperatorSelector, setShowOperatorSelector] =
      React.useState(false);
    const [showValueSelector, setShowValueSelector] = React.useState(false);

    const column = columns.find((column) => column.id === filter.id);

    const operatorListboxId = `${filterItemId}-operator-listbox`;
    const inputId = `${filterItemId}-input`;

    const columnMeta = column?.columnDef.meta;
    const filterOperators =
      columnMeta?.filterOperators ?? getFilterOperators(filter.variant);

    if (!column) {
      return null;
    }

    return (
      <div
        className='flex items-center rounded-md bg-background'
        id={filterItemId}
        key={filter.filterId}
      >
        <Popover onOpenChange={setShowFieldSelector} open={showFieldSelector}>
          <PopoverTrigger asChild>
            <Button
              className='rounded-none rounded-l-md border border-muted-foreground/40 border-r-0 bg-muted/60 px-2 font-normal text-muted-foreground text-xs hover:bg-muted hover:text-foreground dark:bg-input/30'
              variant='ghost'
            >
              {columnMeta?.icon && (
                <columnMeta.icon className='size-4 text-muted-foreground' />
              )}
              {columnMeta?.label ?? column.id}
            </Button>
          </PopoverTrigger>
          <PopoverContent align='start' className='w-48 p-0'>
            <Command loop>
              <CommandInput placeholder={t('search fields')} />
              <CommandList>
                <CommandEmpty>{t('no fields found')}</CommandEmpty>
                <CommandGroup>
                  {columns.map((column) => (
                    <CommandItem
                      key={column.id}
                      onSelect={() => {
                        onFilterUpdate(filter.filterId, {
                          id: column.id as Extract<keyof TData, string>,
                          variant: column.columnDef.meta?.variant ?? 'text',
                          operator: getDefaultFilterOperator(
                            column.columnDef.meta?.variant ?? 'text',
                          ),
                          value: '',
                        });

                        setShowFieldSelector(false);
                      }}
                      value={column.id}
                    >
                      {column.columnDef.meta?.icon && (
                        <column.columnDef.meta.icon />
                      )}
                      <span className='truncate'>
                        {column.columnDef.meta?.label ?? column.id}
                      </span>
                      <Check
                        className={cn(
                          'ml-auto',
                          column.id === filter.id ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Select
          onOpenChange={setShowOperatorSelector}
          onValueChange={(value: FilterOperator) =>
            onFilterUpdate(filter.filterId, {
              operator: value,
              value:
                value === 'isEmpty' || value === 'isNotEmpty'
                  ? ''
                  : filter.value,
            })
          }
          open={showOperatorSelector}
          value={filter.operator}
        >
          <SelectTrigger
            aria-controls={operatorListboxId}
            className='rounded-none border border-muted-foreground/40 border-r-0 bg-muted/60 px-2 py-0 font-normal text-muted-foreground text-xs lowercase hover:bg-muted hover:text-foreground focus-visible:ring-0 dark:bg-input/30 [&_svg]:hidden'
          >
            <SelectValue placeholder={filter.operator} />
          </SelectTrigger>
          <SelectContent id={operatorListboxId}>
            {filterOperators.map((operator) => (
              <SelectItem
                className='lowercase'
                key={operator.value}
                value={operator.value}
              >
                {t(operator.label)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {onFilterInputRender({
          filter,
          column,
          inputId,
          t,
          onFilterUpdate,
          showValueSelector,
          setShowValueSelector,
        })}
        <Button
          aria-controls={filterItemId}
          aria-keyshortcuts='Delete Backspace'
          className='rounded-none rounded-r-md border border-muted-foreground/40 border-l-0 px-1 font-normal text-muted-foreground hover:text-foreground dark:bg-input/30'
          onClick={() => onFilterRemove(filter.filterId)}
          onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
            if (REMOVE_FILTER_SHORTCUTS.includes(event.key.toLowerCase())) {
              event.preventDefault();
              onFilterRemove(filter.filterId);
            }
          }}
          variant='ghost'
        >
          <X className='size-4' />
        </Button>
      </div>
    );
  }
}

type FilterValueSelectorProps<TData> = {
  column: Column<TData>;
  value: string;
  onSelect: (value: string) => void;
};

function FilterValueSelector<TData>({
  column,
  value,
  onSelect,
}: FilterValueSelectorProps<TData>) {
  const { t } = useTranslation(['table']);
  const variant = column.columnDef.meta?.variant ?? 'text';

  switch (variant) {
    case 'boolean':
      return (
        <CommandGroup>
          <CommandItem onSelect={() => onSelect('true')} value='true'>
            True
          </CommandItem>
          <CommandItem onSelect={() => onSelect('false')} value='false'>
            False
          </CommandItem>
        </CommandGroup>
      );

    case 'select':
    case 'multiSelect':
      return (
        <CommandGroup>
          {column.columnDef.meta?.options?.map((option: Option) => (
            <CommandItem
              key={option.value}
              onSelect={() => onSelect(option.value)}
              value={`${option.label} ${option.value}`}
            >
              {option.icon && <option.icon />}
              <span className='truncate'>{option.label}</span>
              {option.count && (
                <span className='ml-auto font-mono text-xs'>
                  {option.count}
                </span>
              )}
            </CommandItem>
          ))}
        </CommandGroup>
      );

    case 'date':
    case 'dateRange':
      return (
        <Calendar
          autoFocus
          captionLayout='dropdown'
          mode='single'
          onSelect={(date) => onSelect(date?.getTime().toString() ?? '')}
          selected={value ? new Date(value) : undefined}
        />
      );

    default: {
      const isEmpty = !value.trim();

      return (
        <CommandGroup>
          <CommandItem
            disabled={isEmpty}
            onSelect={() => onSelect(value)}
            value={value}
          >
            {isEmpty ? (
              <>
                <TextIcon />
                <span>{t('type to add filter...')}</span>
              </>
            ) : (
              <>
                <BadgeCheck />
                <span className='truncate'>
                  {t('filter by')} &quot;{value}&quot;
                </span>
              </>
            )}
          </CommandItem>
        </CommandGroup>
      );
    }
  }
}

type FilterInputRenderProps<TData> = {
  filter: ExtendedColumnFilter<TData>;
  column: Column<TData>;
  inputId: string;
  t: (key: string) => string;
  onFilterUpdate: (
    filterId: string,
    updates: Partial<Omit<ExtendedColumnFilter<TData>, 'filterId'>>,
  ) => void;
  showValueSelector: boolean;
  setShowValueSelector: (value: boolean) => void;
};

function renderEmptyFilterInput<TData>({
  filter,
  column,
  inputId,
}: Pick<FilterInputRenderProps<TData>, 'filter' | 'column' | 'inputId'>) {
  return (
    <output
      aria-label={`${column.columnDef.meta?.label} filter is ${
        filter.operator === 'isEmpty' ? 'empty' : 'not empty'
      }`}
      aria-live='polite'
      className='w-px self-stretch border-0 border-muted-foreground/40 border-l'
      id={inputId}
    />
  );
}

function renderTextNumberRangeFilter<TData>({
  filter,
  column,
  inputId,
  onFilterUpdate,
}: Pick<
  FilterInputRenderProps<TData>,
  'filter' | 'column' | 'inputId' | 'onFilterUpdate'
>) {
  const isRangeBetween =
    (filter.variant === 'range' && filter.operator === 'isBetween') ||
    filter.operator === 'isBetween';

  if (isRangeBetween) {
    return (
      <DataTableRangeFilter
        className="size-full max-w-28 gap-0 **:data-[slot='range-min']:border-r-0 [&_input]:rounded-none [&_input]:px-1.5 [&_input]:text-xs"
        column={column}
        filter={filter}
        inputId={inputId}
        onFilterUpdate={onFilterUpdate}
      />
    );
  }

  const isNumber = filter.variant === 'number' || filter.variant === 'range';

  return (
    <Input
      className='w-20 rounded-none border border-muted-foreground/40 px-1.5 text-xs shadow-none focus-visible:ring-0'
      defaultValue={typeof filter.value === 'string' ? filter.value : ''}
      id={inputId}
      inputMode={isNumber ? 'numeric' : undefined}
      onChange={(event) =>
        onFilterUpdate(filter.filterId, { value: event.target.value })
      }
      placeholder={column.columnDef.meta?.placeholder ?? 'Enter value...'}
      type={isNumber ? 'number' : 'text'}
    />
  );
}

function renderBooleanFilter<TData>({
  filter,
  inputId,
  onFilterUpdate,
  showValueSelector,
  setShowValueSelector,
}: Pick<
  FilterInputRenderProps<TData>,
  | 'filter'
  | 'inputId'
  | 'onFilterUpdate'
  | 'showValueSelector'
  | 'setShowValueSelector'
>) {
  const inputListboxId = `${inputId}-listbox`;

  return (
    <Select
      onOpenChange={setShowValueSelector}
      onValueChange={(value: 'true' | 'false') =>
        onFilterUpdate(filter.filterId, { value })
      }
      open={showValueSelector}
      value={typeof filter.value === 'string' ? filter.value : 'true'}
    >
      <SelectTrigger
        aria-controls={inputListboxId}
        className='rounded-none bg-transparent px-1.5 text-xs [&_svg]:hidden'
        id={inputId}
      >
        <SelectValue placeholder={filter.value ? 'True' : 'False'} />
      </SelectTrigger>
      <SelectContent id={inputListboxId}>
        <SelectItem value='true'>True</SelectItem>
        <SelectItem value='false'>False</SelectItem>
      </SelectContent>
    </Select>
  );
}

function renderSelectFilter<TData>({
  filter,
  column,
  inputId,
  onFilterUpdate,
  showValueSelector,
  setShowValueSelector,
  t,
}: FilterInputRenderProps<TData>) {
  const inputListboxId = `${inputId}-listbox`;
  const options = column.columnDef.meta?.options ?? [];
  const selectedValues = Array.isArray(filter.value)
    ? filter.value
    : [filter.value];
  const selectedOptions = options.filter((option: Option) =>
    selectedValues.includes(option.value),
  );

  const handleOptionSelect = (optionValue: string) => {
    const value =
      filter.variant === 'multiSelect'
        ? selectedValues.includes(optionValue)
          ? selectedValues.filter((v) => v !== optionValue)
          : [...selectedValues, optionValue]
        : optionValue;
    onFilterUpdate(filter.filterId, { value });
  };
  return (
    <Popover onOpenChange={setShowValueSelector} open={showValueSelector}>
      <PopoverTrigger asChild>
        <Button
          aria-controls={inputListboxId}
          className='min-w-14 rounded-none border border-muted-foreground/40 px-1.5 font-normal text-xs dark:bg-input/30'
          id={inputId}
          variant='ghost'
        >
          {selectedOptions.length === 0 ? (
            filter.variant === 'multiSelect' ? (
              'Select...'
            ) : (
              'Select...'
            )
          ) : (
            <>
              <div className='flex items-center -space-x-1.5 rtl:space-x-reverse'>
                {selectedOptions.map((selectedOption: Option) =>
                  selectedOption.icon ? (
                    <div
                      className='rounded-full border bg-background p-0.5'
                      key={selectedOption.value}
                    >
                      <selectedOption.icon className='size-4' />
                    </div>
                  ) : null,
                )}
              </div>
              <span className='truncate'>
                {selectedOptions.length > 1
                  ? `${selectedOptions.length} ${t('selected')}`
                  : selectedOptions[0]?.label}
              </span>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align='start' className='w-48 p-0' id={inputListboxId}>
        <Command>
          <CommandInput placeholder={t('search options')} />
          <CommandList>
            <CommandEmpty>{t('no options found')}</CommandEmpty>
            <CommandGroup>
              {options.map((option: Option) => (
                <CommandItem
                  key={option.value}
                  onSelect={() => handleOptionSelect(option.value)}
                  value={`${option.label} ${option.value}`}
                >
                  {option.icon && <option.icon />}
                  <span className='truncate' title={option.label}>
                    {option.label}
                  </span>
                  {filter.variant === 'multiSelect' && (
                    <Check
                      className={cn(
                        'ml-auto',
                        selectedValues.includes(option.value)
                          ? 'opacity-100'
                          : 'opacity-0',
                      )}
                    />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function renderDateFilter<TData>({
  filter,
  inputId,
  onFilterUpdate,
  showValueSelector,
  setShowValueSelector,
}: Pick<
  FilterInputRenderProps<TData>,
  | 'filter'
  | 'inputId'
  | 'onFilterUpdate'
  | 'showValueSelector'
  | 'setShowValueSelector'
>) {
  const inputListboxId = `${inputId}-listbox`;
  const dateValue = Array.isArray(filter.value)
    ? filter.value.filter(Boolean)
    : [filter.value, filter.value].filter(Boolean);

  const displayValue =
    filter.operator === 'isBetween' && dateValue.length === 2
      ? `${formatDate(new Date(Number(dateValue[0])))} - ${formatDate(
          new Date(Number(dateValue[1])),
        )}`
      : dateValue[0]
        ? formatDate(new Date(Number(dateValue[0])))
        : 'Pick date...';

  const handleRangeSelect = (date: { from?: Date; to?: Date } | undefined) => {
    onFilterUpdate(filter.filterId, {
      value: date
        ? [
            (date.from?.getTime() ?? '').toString(),
            (date.to?.getTime() ?? '').toString(),
          ]
        : [],
    });
  };

  const handleSingleSelect = (date: Date | undefined) => {
    onFilterUpdate(filter.filterId, {
      value: (date?.getTime() ?? '').toString(),
    });
  };

  return (
    <Popover onOpenChange={setShowValueSelector} open={showValueSelector}>
      <PopoverTrigger asChild>
        <Button
          aria-controls={inputListboxId}
          className={cn(
            'rounded-none border border-muted-foreground/40 px-1.5 font-normal text-xs dark:bg-input/30',
            !filter.value && 'text-muted-foreground',
          )}
          id={inputId}
          variant='ghost'
        >
          <CalendarIcon className='size-4' />
          <span className='truncate'>{displayValue}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align='start' className='w-auto p-0' id={inputListboxId}>
        {filter.operator === 'isBetween' ? (
          <Calendar
            autoFocus
            captionLayout='dropdown'
            mode='range'
            onSelect={handleRangeSelect}
            selected={
              dateValue.length === 2
                ? {
                    from: new Date(Number(dateValue[0])),
                    to: new Date(Number(dateValue[1])),
                  }
                : {
                    from: new Date(),
                    to: new Date(),
                  }
            }
          />
        ) : (
          <Calendar
            autoFocus
            captionLayout='dropdown'
            mode='single'
            onSelect={handleSingleSelect}
            selected={dateValue[0] ? new Date(Number(dateValue[0])) : undefined}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

function onFilterInputRender<TData>(props: FilterInputRenderProps<TData>) {
  const { filter } = props;

  if (filter.operator === 'isEmpty' || filter.operator === 'isNotEmpty') {
    return renderEmptyFilterInput(props);
  }

  switch (filter.variant) {
    case 'text':
    case 'number':
    case 'range':
      return renderTextNumberRangeFilter(props);
    case 'boolean':
      return renderBooleanFilter(props);
    case 'select':
    case 'multiSelect':
      return renderSelectFilter(props);
    case 'date':
    case 'dateRange':
      return renderDateFilter(props);
    default:
      return null;
  }
}
